import React, { useState, useEffect, useMemo } from 'react';
import { api } from '@/App';
import usePeriodChange from '@/hooks/usePeriodChange';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, Wallet, DollarSign, AlertCircle, Package, Building2, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { captureElement } from '@/lib/captureElement';
import { computePartaiRecap } from '@/lib/partaiRecap';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useDashboardData } from '@/hooks/useDashboardData';
import { useBalanceSheetCalculations } from '@/hooks/useBalanceSheetCalculations';
import { useBranchDividends } from '@/hooks/useBranchDividends';
import { usePeriodTransfers } from '@/hooks/usePeriodTransfers';
import { usePreviousPeriodRetainedEarnings } from '@/hooks/usePreviousPeriodRetainedEarnings';
import { useBalanceSheet } from '@/contexts/BalanceSheetContext';

// Import dashboard sub-components
import { 
  LoadingSpinner, 
  YearlySummaryTable, 
  YearlyModalChart,
  BalanceWarning, 
  BalanceSheetCard,
  InventoryDialog,
  TransferDialog,
  ImportStokDialog 
} from '@/components/dashboard';

const Dashboard = ({ user }) => {
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [periodData, setPeriodData] = useState(null);
  const [branchDividends, setBranchDividends] = useState([]);
  const [branchesWithManager, setBranchesWithManager] = useState([]);
  const [branchesWithoutManager, setBranchesWithoutManager] = useState([]);
  const [branchesByManager, setBranchesByManager] = useState({}); // NEW: Group by manager
  const [managers, setManagers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [periodTransfers, setPeriodTransfers] = useState({ total: 0, details: [] });
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [selectedTransferDetail, setSelectedTransferDetail] = useState(null);
  const [previousPeriodRetainedEarnings, setPreviousPeriodRetainedEarnings] = useState(null);
  const [branchSearchFilter, setBranchSearchFilter] = useState('');
  const [inventoryDetails, setInventoryDetails] = useState([]);
  const [showInventoryDialog, setShowInventoryDialog] = useState(false);
  const [inventorySearchFilter, setInventorySearchFilter] = useState('');
  const [yearlySummary, setYearlySummary] = useState([]);
  const [loadingYearlySummary, setLoadingYearlySummary] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  // Filter state for Laba Rugi tables
  const [labaRugiSearch, setLabaRugiSearch] = useState('');
  const [labaRugiShowAll, setLabaRugiShowAll] = useState(false);
  const labaRugiRef = React.useRef(null);
  const [capturingLabaRugi, setCapturingLabaRugi] = useState(false);
  const [mitraDialog, setMitraDialog] = useState({ open: false, mitra: null, loading: false, rows: [] });
  const [partaiRecap, setPartaiRecap] = useState(null);

  useEffect(() => { setPartaiRecap(null); }, [selectedPeriod]);

  const loadPartaiRecap = async () => {
    if (partaiRecap) return partaiRecap;
    const periodId = selectedPeriod || localStorage.getItem('selectedPeriodId');
    const params = periodId ? { period_id: periodId } : {};
    const [branchesRes, purchasesRes, salesRes, expensesRes, settingsRes, periodsRes] = await Promise.all([
      api.get('/branches'),
      api.get('/purchases', { params }),
      api.get('/sales', { params }),
      api.get('/expenses', { params }),
      api.get('/settings'),
      api.get('/periods'),
    ]);
    const currentPeriod = (periodsRes.data || []).find(p => p.id === periodId);
    const defaultCost = currentPeriod?.default_operational_cost || settingsRes.data?.default_operational_cost || 0;
    const overrideMap = {};
    (settingsRes.data?.branch_operational_overrides || []).forEach((o) => { overrideMap[o.branch_id] = o.cost_per_gr; });
    const recap = computePartaiRecap({
      branches: branchesRes.data,
      purchases: purchasesRes.data,
      sales: salesRes.data,
      expenses: expensesRes.data,
      defaultCost,
      overrideMap,
    });
    setPartaiRecap(recap);
    return recap;
  };

  const openMitraDialog = async (mitraName) => {
    setMitraDialog({ open: true, mitra: mitraName, loading: true, rows: [] });
    try {
      const recap = await loadPartaiRecap();
      const allBranches = [
        ...(branchesWithoutManager || []),
        ...Object.values(branchesByManager || {}).flat(),
      ];
      const matched = filterBranchesForLabaRugi(allBranches);
      const groups = [];
      matched.forEach((b) => {
        const sh = (b.shareholders || []).find(s => s.manager_name === mitraName && s.percentage > 0);
        if (!sh) return;
        const partaiRows = recap
          .filter(r => r.branch_id === b.branchId)
          .map(r => ({
            partai: r.partai || 'Tanpa Partai',
            labaRugi: r.labaRugi,
            dividen: Math.round(r.labaRugi * (sh.percentage / 100)),
          }))
          .sort((x, y) => x.partai.localeCompare(y.partai));
        groups.push({
          branchId: b.branchId,
          branchName: b.branchName,
          percentage: sh.percentage,
          rows: partaiRows,
        });
      });
      setMitraDialog({ open: true, mitra: mitraName, loading: false, rows: groups });
    } catch (error) {
      toast.error('Gagal memuat rincian partai');
      setMitraDialog({ open: false, mitra: null, loading: false, rows: [] });
    }
  };


  const handleCaptureLabaRugi = async () => {
    setCapturingLabaRugi(true);
    await new Promise((resolve) => setTimeout(resolve, 100));
    await captureElement(labaRugiRef.current, `Laba-Rugi${periodData ? '-' + periodData.name : ''}`);
    setCapturingLabaRugi(false);
  };

  useEffect(() => {
    const initDashboard = async () => {
      // Get initial selected period from localStorage
      let periodId = localStorage.getItem('selectedPeriodId');
      
      // If no period in localStorage, fetch active period
      if (!periodId) {
        try {
          const activeRes = await api.get('/periods/active');
          if (activeRes.data && activeRes.data.id) {
            periodId = activeRes.data.id;
            localStorage.setItem('selectedPeriodId', periodId);
            
            // Extract year directly from active period
            if (activeRes.data.name) {
              const parts = activeRes.data.name.split(' ');
              if (parts.length === 2) {
                const year = parseInt(parts[1]);
                if (!isNaN(year)) {
                  setSelectedYear(year);
                }
              }
            }
          }
        } catch (error) {
          console.error('[Dashboard] Failed to fetch active period:', error);
        }
      }
      
      if (periodId) {
        setSelectedPeriod(periodId);
        fetchBalanceSheet(periodId);
        fetchPeriodData(periodId);
      }
    };
    
    initDashboard();
  }, []);

  usePeriodChange((event) => {
    const periodId = event.detail.periodId;
    setSelectedPeriod(periodId);
    fetchBalanceSheet(periodId);
    fetchPeriodData(periodId);
  });

  // Fetch yearly summary when periodData changes
  useEffect(() => {
    if (!periodData) return;

    // Prefer extracting year from the period's `months` array (format YYYY-MM),
    // which reflects the actual months the period covers. Fall back to parsing
    // the period name (e.g. "Januari 2026") for legacy periods without months.
    let year = null;
    const months = Array.isArray(periodData.months) ? periodData.months : [];
    if (months.length > 0) {
      const firstMonth = months[0];
      const y = parseInt(String(firstMonth).slice(0, 4));
      if (!isNaN(y)) year = y;
    }
    if (year === null && periodData.name) {
      const parts = periodData.name.split(' ');
      if (parts.length >= 2) {
        const y = parseInt(parts[parts.length - 1]);
        if (!isNaN(y)) year = y;
      }
    }
    if (year !== null) {
      console.log('[Dashboard] Resolved year for yearly-summary:', year, 'from', periodData.name, 'months:', months);
      setSelectedYear(year);
    }
  }, [periodData]);

  // Fetch yearly summary when selectedYear changes
  useEffect(() => {
    const doFetch = async () => {
      if (!selectedYear) return;
      
      console.log('[Dashboard] Fetching yearly summary for year:', selectedYear);
      setLoadingYearlySummary(true);
      try {
        const response = await api.get(`/periods/yearly-summary?year=${selectedYear}`);
        console.log('[Dashboard] Yearly summary received:', response.data);
        setYearlySummary(response.data);
      } catch (error) {
        console.error('Failed to fetch yearly summary:', error);
        toast.error('Gagal memuat ringkasan tahunan');
      } finally {
        setLoadingYearlySummary(false);
      }
    };
    
    doFetch();
  }, [selectedYear]);

  // Fetch retained earnings for previous period when needed
  useEffect(() => {
    const fetchPreviousPeriodRetainedEarnings = async () => {
      if (!previousPeriodRetainedEarnings || !previousPeriodRetainedEarnings.loading) {
        return;
      }

      try {
        // Get all periods to find previous period ID
        const allPeriodsRes = await api.get('/periods');
        const allPeriods = allPeriodsRes.data.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
        const previousPeriod = allPeriods.find(p => p.name === previousPeriodRetainedEarnings.periodName);
        
        if (!previousPeriod) {
          setPreviousPeriodRetainedEarnings(null);
          return;
        }

        // Fetch data for previous period
        const params = { period_id: previousPeriod.id };
        const [purchasesRes, salesRes, expensesRes, centralExpensesRes, managersRes, branchesRes] = await Promise.all([
          api.get('/purchases', { params }),
          api.get('/sales', { params }),
          api.get('/expenses', { params }),
          api.get('/central-expenses', { params }),
          api.get('/managers'),
          api.get('/branches')
        ]);

        const purchases = purchasesRes.data;
        const sales = salesRes.data;
        const expenses = expensesRes.data;
        const centralExpenses = centralExpensesRes.data;
        const managersData = managersRes.data;
        const branchesData = branchesRes.data;

        // Calculate branch dividends (simplified version)
        const branchProfits = {};
        branchesData.forEach(branch => {
          const branchPurchases = purchases.filter(p => p.branch_id === branch.id);
          const branchSales = sales.filter(s => s.branch_id === branch.id);
          const branchExpenses = expenses.filter(e => e.branch_id === branch.id);

          const revenue = branchSales.reduce((sum, s) => sum + (s.total_amount || 0), 0);
          const cost = branchPurchases.reduce((sum, p) => sum + (p.total_amount || 0), 0);
          const expense = branchExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
          
          branchProfits[branch.id] = {
            profit: revenue - cost - expense,
            shareholders: []
          };

          // Get shareholders
          managersData.forEach(manager => {
            const share = manager.branch_shares?.find(s => s.branch_id === branch.id);
            if (share) {
              branchProfits[branch.id].shareholders.push({
                percentage: share.percentage
              });
            }
          });
        });

        // Calculate Pusat (Central) profit
        // NOTE: central-expenses can have NEGATIVE values which represent REVENUE (pendapatan)
        const centralRevenue = centralExpenses
          .filter(e => e.amount < 0)
          .reduce((sum, e) => sum + Math.abs(e.amount), 0);
        const centralActualExpenses = centralExpenses
          .filter(e => e.amount > 0)
          .reduce((sum, e) => sum + e.amount, 0);
        
        const pusatProfit = centralRevenue - centralActualExpenses;

        // Calculate retained earnings
        let totalMitraDividend = 0;
        let totalInvestorDividend = 0;

        // From branches
        Object.values(branchProfits).forEach(branchProfit => {
          const totalSharePercentage = branchProfit.shareholders.reduce((sum, s) => sum + s.percentage, 0);
          const investorPercentage = 100 - totalSharePercentage;
          
          totalMitraDividend += (totalSharePercentage / 100) * branchProfit.profit;
          totalInvestorDividend += (investorPercentage / 100) * branchProfit.profit;
        });

        // From Pusat - 100% goes to investor
        totalInvestorDividend += pusatProfit;

        setPreviousPeriodRetainedEarnings({
          periodName: previousPeriod.name,
          labaDitahanMitra: totalMitraDividend,
          labaDitahanInvestor: totalInvestorDividend,
          loading: false
        });
      } catch (error) {
        console.error('Failed to fetch previous period retained earnings:', error);
        setPreviousPeriodRetainedEarnings(null);
      }
    };

    fetchPreviousPeriodRetainedEarnings();
  }, [previousPeriodRetainedEarnings]);

  // Calculate balance difference with periodTransfers and previous period retained earnings included
  const actualBalanceDifference = React.useMemo(() => {
    if (balanceSheet && balanceSheet.totalAssets !== undefined) {
      const totalAssetsWithTransfers = balanceSheet.totalAssets + periodTransfers.total;
      const previousPeriodTotal = (previousPeriodRetainedEarnings && !previousPeriodRetainedEarnings.loading) 
        ? previousPeriodRetainedEarnings.labaDitahanMitra + previousPeriodRetainedEarnings.labaDitahanInvestor 
        : 0;
      const totalEquityWithPrevious = balanceSheet.totalEquity + previousPeriodTotal;
      return totalAssetsWithTransfers - (balanceSheet.totalLiabilities + totalEquityWithPrevious);
    }
    return 0;
  }, [balanceSheet, periodTransfers.total, previousPeriodRetainedEarnings]);

  // Strict balance check: ASET wajib SAMA dengan KEWAJIBAN + MODAL.
  // Threshold 0.005 menyaring float-noise dari aritmetika JS (1e-10 dst.) tapi tetap
  // menandai selisih ≥ 0,01 (sen) — termasuk contoh user "selisih 0,1" — sebagai tidak seimbang.
  const isActuallyBalanced = Math.abs(actualBalanceDifference) < 0.005;

  // True only when ALL data feeding the balance sheet has finished loading.
  // Used to suppress the "tidak seimbang" warnings during initial mount/data fetch.
  const balanceDataReady = !loading
    && !(previousPeriodRetainedEarnings && previousPeriodRetainedEarnings.loading)
    && balanceSheet && balanceSheet.totalAssets !== undefined;

  // Publish balance status to the global context so every other page can render
  // the GlobalBalanceBanner if the sheet is unbalanced.
  // Guard via `balanceDataReady` so partial-load values don't briefly flash a wrong status.
  const { publishBalance } = useBalanceSheet();
  useEffect(() => {
    if (!balanceDataReady) return;
    publishBalance({
      isBalanced: isActuallyBalanced,
      difference: actualBalanceDifference,
      periodId: selectedPeriod,
    });
  }, [balanceDataReady, isActuallyBalanced, actualBalanceDifference, selectedPeriod, publishBalance]);

  // Cleanup dialog state on unmount
  useEffect(() => {
    return () => {
      setShowTransferDialog(false);
      setSelectedTransferDetail(null);
    };
  }, []);

  const fetchPeriodData = async (periodId) => {
    if (!periodId) {
      setPeriodData(null);
      return;
    }
    
    try {
      const periodRes = await api.get(`/periods/${periodId}`);
      console.log('[Dashboard] fetchPeriodData received:', periodRes.data);
      setPeriodData(periodRes.data);
      
      // Also extract year here as backup
      if (periodRes.data && periodRes.data.name) {
        const parts = periodRes.data.name.split(' ');
        if (parts.length === 2) {
          const year = parseInt(parts[1]);
          console.log('[Dashboard] Setting year from fetchPeriodData:', year);
          if (!isNaN(year)) {
            setSelectedYear(year);
          }
        }
      }
      
      // Fetch all periods to find previous period
      const allPeriodsRes = await api.get('/periods');
      const allPeriods = allPeriodsRes.data.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
      const currentPeriodIndex = allPeriods.findIndex(p => p.id === periodId);
      
      // Find previous period (must be open/not closed)
      let previousPeriod = null;
      if (currentPeriodIndex >= 0 && currentPeriodIndex < allPeriods.length - 1) {
        const potentialPrevPeriod = allPeriods[currentPeriodIndex + 1];
        if (!potentialPrevPeriod.is_closed) {
          previousPeriod = potentialPrevPeriod;
        }
      }
      
      // If there's a previous open period, calculate its retained earnings
      if (previousPeriod) {
        try {
          // This will trigger a separate balance sheet calculation for previous period
          // We'll store the result in a separate state
          console.log('[Dashboard] Found previous open period:', previousPeriod.name);
          setPreviousPeriodRetainedEarnings({ 
            periodName: previousPeriod.name,
            loading: true 
          });
          // The actual calculation will happen in fetchBalanceSheet
        } catch (error) {
          console.error('Failed to process previous period:', error);
          setPreviousPeriodRetainedEarnings(null);
        }
      } else {
        setPreviousPeriodRetainedEarnings(null);
      }
      
      // Fetch period transfers (outgoing only - one-way to future periods)
      try {
        const transfersRes = await api.get(`/period-transfers`, {
          params: { source_period_id: periodId }
        });
        
        console.log('[Dashboard] Period transfers fetched:', transfersRes.data);
        
        // Filter out PURCHASE_TO_STOCK and IMPORT_STOCK transfers
        // These are stock conversions/imports, not period transfers
        // PURCHASE_TO_STOCK = "Jadikan Stok" feature
        // IMPORT_STOCK = "Import Stok" feature (adds inventory only, not period transfer)
        const actualPeriodTransfers = transfersRes.data.filter(t => 
          t.transfer_type !== 'PURCHASE_TO_STOCK' && t.transfer_type !== 'IMPORT_STOCK'
        );
        
        // Calculate total and prepare details
        // IMPORTANT: Sales transfers should SUBTRACT (negative), not add
        // Because sales = revenue, moving sales OUT means losing revenue (negative impact on assets)
        const total = actualPeriodTransfers.reduce((sum, transfer) => {
          if (transfer.transfer_type === 'SALE') {
            return sum - (transfer.total_amount || 0);  // Subtract sales
          }
          return sum + (transfer.total_amount || 0);  // Add purchases/expenses
        }, 0);
        const details = actualPeriodTransfers.map(transfer => ({
          id: transfer.id,
          date: transfer.timestamp,
          type: transfer.transfer_type,
          entity_count: transfer.entity_count,
          amount: transfer.total_amount,
          total_weight: transfer.total_weight || 0,
          target_period_id: transfer.target_period_id,
          branch_id: transfer.branch_id,
          description: transfer.description
        }));
        
        console.log('[Dashboard] Period transfers total:', total);
        console.log('[Dashboard] Period transfers count:', details.length);
        
        // CLOSED period: pakai nilai Pindah Periode yang dibekukan saat tutup buku.
        // Entri transfer masih bisa bergeser setelah periode ditutup, sementara pos aset
        // lain sudah beku dari snapshot - memakai angka live membuat neraca tidak seimbang.
        let frozenTotal = null;
        try {
          const snapRes = await api.get(`/periods/${periodId}/snapshot`);
          const snapTotal = snapRes.data?.assets_snapshot?.period_transfers_total;
          if (snapRes.data?.has_snapshot && snapTotal !== undefined && snapTotal !== null) {
            frozenTotal = snapTotal;
          }
        } catch (snapError) {
          console.error('Failed to fetch period snapshot for transfers:', snapError);
        }
        
        setPeriodTransfers({ total: frozenTotal !== null ? frozenTotal : total, details });
      } catch (transferError) {
        console.error('Failed to fetch period transfers:', transferError);
        setPeriodTransfers({ total: 0, details: [] });
      }
    } catch (error) {
      console.error('Failed to fetch period data:', error);
    }
  };

  const fetchBalanceSheet = async (periodId = null) => {
    setLoading(true);
    try {
      const params = periodId ? { period_id: periodId } : {};

      // Fetch all data needed for balance sheet and dividend table
      // Also fetch snapshot if period is closed
      const [accountsRes, purchasesRes, salesRes, expensesRes, investorsRes, branchesRes, managersRes, centralExpensesRes, snapshotRes] = await Promise.all([
        api.get('/accounts/all'),
        api.get('/purchases', { params }),
        api.get('/sales', { params }),
        api.get('/expenses', { params }),
        api.get('/investors'),
        api.get('/branches'),
        api.get('/managers'),
        api.get('/central-expenses', { params }),
        periodId ? api.get(`/periods/${periodId}/snapshot`).catch(() => ({ data: { has_snapshot: false } })) : Promise.resolve({ data: { has_snapshot: false } })
      ]);

      const allAccounts = accountsRes.data;
      const purchases = purchasesRes.data;
      const sales = salesRes.data;
      const expenses = expensesRes.data;
      const investors = investorsRes.data;
      const branchesData = branchesRes.data;
      const managersData = managersRes.data;
      const centralExpenses = centralExpensesRes.data;
      const snapshotData = snapshotRes.data;
      
      // Store managers and branches in state for use in render
      setManagers(managersData);
      setBranches(branchesData);

      // Helper function to get shareholders for a branch
      const getBranchShareholders = (branchId) => {
        const shareholders = [];
        managersData.forEach(manager => {
          const share = manager.branch_shares?.find(s => s.branch_id === branchId);
          if (share) {
            shareholders.push({
              manager_id: manager.id,
              manager_name: manager.name,
              percentage: share.percentage
            });
          }
        });
        return shareholders.sort((a, b) => b.percentage - a.percentage);
      };

      // === ASSETS (ASET) ===
      // For CLOSED period with assets_snapshot: use snapshot values so balance sheet
      // stays in-balance even after subsequent periods accumulate activity.
      const useAssetsSnapshot = snapshotData.has_snapshot && snapshotData.assets_snapshot;
      const assetsSnap = useAssetsSnapshot ? snapshotData.assets_snapshot : null;

      const centralBalance = useAssetsSnapshot
        ? (assetsSnap.central_balance || 0)
        : allAccounts
            .filter(acc => acc.account_type === 'CENTRAL')
            .reduce((sum, acc) => sum + (acc.balance || 0), 0);
      
      // Hutang Usaha is NOT part of ASET anymore - moved to KEWAJIBAN
      const hutangUsahaBalance = useAssetsSnapshot
        ? (assetsSnap.hutang_usaha || 0)
        : allAccounts
            .filter(acc => acc.account_type === 'HUTANG_USAHA' || acc.account_type === 'OTHER')
            .reduce((sum, acc) => sum + (acc.balance || 0), 0);
      
      const branchBalance = useAssetsSnapshot
        ? (assetsSnap.branch_balance || 0)
        : allAccounts
            .filter(acc => acc.account_type === 'BRANCH')
            .reduce((sum, acc) => sum + (acc.balance || 0), 0);
      
      // Manager (Laba Rugi Mitra) is NOT part of ASET - moved to KEWAJIBAN
      let labaRugiMitraBalance;
      if (snapshotData.has_snapshot && snapshotData.mitra_labarugi) {
        // Use snapshot data for closed period
        labaRugiMitraBalance = snapshotData.mitra_labarugi.reduce((sum, m) => sum + (m.closing_balance || 0), 0);
        console.log('[Dashboard] Using SNAPSHOT data for Laba Rugi Mitra:', labaRugiMitraBalance);
      } else {
        // Dynamic calculation for active period
        labaRugiMitraBalance = allAccounts
          .filter(acc => acc.account_type === 'MANAGER')
          .reduce((sum, acc) => sum + (acc.balance || 0), 0);
        console.log('[Dashboard] Using DYNAMIC data for Laba Rugi Mitra:', labaRugiMitraBalance);
      }
      
      const customerBalance = useAssetsSnapshot
        ? (assetsSnap.customer_balance || 0)
        : allAccounts
            .filter(acc => acc.account_type === 'CUSTOMER')
            .reduce((sum, acc) => sum + (acc.balance || 0), 0);
      
      // Fetch pending money for Dana Gantung (or snapshot for closed periods)
      let pendingMoney = 0;
      if (useAssetsSnapshot) {
        pendingMoney = assetsSnap.pending_money || 0;
      } else {
        try {
          const pendingRes = await api.get('/pending-money');
          pendingMoney = pendingRes.data
            .filter(p => p.status === 'PENDING')
            .reduce((sum, p) => {
              // PENERIMA = minus (dana pending berkurang), PENGIRIM = plus (dana pending bertambah)
              const effectiveAmount = p.jenis_transaksi === 'PENERIMA' ? -(p.amount || 0) : (p.amount || 0);
              return sum + effectiveAmount;
            }, 0);
        } catch (error) {
          console.error('Failed to fetch pending money:', error);
        }
      }
      
      // ASET: Kas & Bank only (Rekening Pusat + Rekening Cabang + Dana Gantung)
      const totalCash = centralBalance + branchBalance + pendingMoney;
      
      // 2. Inventory (Persediaan) - Stock from sales (period-filtered; stable for closed periods)
      const inventoryValue = useAssetsSnapshot
        ? (assetsSnap.inventory_value || 0)
        : sales
            .filter(s => s.keterangan === 'Stok')
            .reduce((sum, s) => sum + (s.total_amount || 0), 0);
      
      // 3. Receivables (Piutang) - Customer accounts with positive balance
      const receivables = customerBalance;
      
      const totalCurrentAssets = totalCash + inventoryValue + receivables;
      // Note: periodTransfers.total will be added when rendering/displaying
      // The calculation here is base assets without period transfers
      const totalAssets = totalCurrentAssets;

      // === LIABILITIES (KEWAJIBAN) ===
      
      // Hutang Usaha - Total saldo from Hutang Usaha accounts
      const hutangUsaha = hutangUsahaBalance;
      
      // Laba Rugi Mitra - Total saldo from Manager accounts
      const labaRugiMitra = labaRugiMitraBalance;
      
      const totalLiabilities = hutangUsaha + labaRugiMitra;

      // === EQUITY (MODAL) ===
      
      // 1. Investor Capital (Modal Investor) 
      // Use snapshot data if period is closed and has snapshot
      let investorCapital;
      if (snapshotData.has_snapshot && snapshotData.investor_modal) {
        // Use snapshot data for closed period
        investorCapital = snapshotData.investor_modal.reduce((sum, inv) => sum + (inv.closing_balance || 0), 0);
        console.log('[Dashboard] Using SNAPSHOT data for Modal Investor:', investorCapital);
      } else {
        // Dynamic calculation for active period
        investorCapital = investors.reduce((sum, inv) => sum + (inv.balance || 0), 0);
        console.log('[Dashboard] Using DYNAMIC data for Modal Investor:', investorCapital);
      }
      
      // 2. Retained Earnings (Laba Ditahan) - Will be calculated after dividend table
      const totalRevenue = sales.reduce((sum, s) => sum + (s.total_amount || 0), 0);
      const totalPurchases = purchases.reduce((sum, p) => sum + (p.total_amount || 0), 0);
      const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
      const totalCentralExpenses = centralExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
      const retainedEarnings = totalRevenue - totalPurchases - totalExpenses - totalCentralExpenses;

      // === CALCULATE BRANCH DIVIDENDS ===
      
      // Calculate PUSAT (Central) profit/loss
      // NOTE: central-expenses can have NEGATIVE values which represent REVENUE (pendapatan)
      // and POSITIVE values which represent actual EXPENSES
      // So we need to separate them
      const centralRevenue = centralExpenses
        .filter(e => e.amount < 0)
        .reduce((sum, e) => sum + Math.abs(e.amount), 0); // Convert negative to positive
      const centralActualExpenses = centralExpenses
        .filter(e => e.amount > 0)
        .reduce((sum, e) => sum + e.amount, 0);
      
      const pusatProfit = centralRevenue - centralActualExpenses;
      
      // Create PUSAT row (positioned first)
      const pusatRow = {
        branchId: 'PUSAT',
        branchName: 'PUSAT',
        profitLoss: pusatProfit,
        shareholders: [],
        totalSharePercentage: 0,
        mitraDividend: 0,
        investorDividend: pusatProfit  // All profit goes to investors
      };
      
      const dividendData = branchesData.map((branch) => {
        // Get branch sales, purchases, and expenses
        const branchSales = sales.filter(s => s.branch_id === branch.id);
        const branchPurchases = purchases.filter(p => p.branch_id === branch.id);
        const branchExpenses = expenses.filter(e => e.branch_id === branch.id);

        // Calculate profit/loss for this branch (rounded to integer for consistent dividend calc)
        const revenue = branchSales.reduce((sum, s) => sum + (s.total_amount || 0), 0);
        const purchaseCost = branchPurchases.reduce((sum, p) => sum + (p.total_amount || 0), 0);
        const expenseCost = branchExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
        const profitLoss = Math.round(revenue - purchaseCost - expenseCost);

        // Get shareholders data for this branch from managers
        const shareholders = getBranchShareholders(branch.id);

        // Calculate dividend for each shareholder (individually rounded)
        const shareholdersWithDividend = shareholders.map(sh => ({
          ...sh,
          dividend: Math.round((sh.percentage / 100) * profitLoss)
        }));

        // Find managers with shares in this branch (position = 'Manager')
        const managersInBranch = shareholdersWithDividend.filter(sh => {
          const manager = managersData.find(m => m.id === sh.manager_id);
          return manager && manager.position === 'Manager';
        });
        
        // Find non-manager mitra (position = 'Mitra')
        const mitraOnly = shareholdersWithDividend.filter(sh => {
          const manager = managersData.find(m => m.id === sh.manager_id);
          return manager && manager.position === 'Mitra';
        });
        
        // Step 1: Calculate Mitra dividend as SUM of individually-rounded mitra dividends
        // (avoids desimal drift: matches exactly the per-shareholder "Dividen" column)
        const mitraDividend = mitraOnly.reduce((sum, m) => sum + (m.dividend || 0), 0);
        
        // Step 2: Profit after Mitra = Laba Rugi - Dividen Mitra (sequential, as user requested)
        const profitAfterMitra = profitLoss - mitraDividend;
        
        // Step 3: Calculate Manager info (sum of individually-rounded manager shareholders)
        const totalManagerPercentage = managersInBranch.reduce((sum, m) => sum + (m.percentage || 0), 0);
        const managerDividend = managersInBranch.reduce((sum, m) => sum + (m.dividend || 0), 0);
        
        // Calculate total mitra+manager dividend for all shareholders
        const totalSharePercentage = shareholders.reduce((sum, sh) => sum + (sh.percentage || 0), 0);
        
        // Sisa Dividen = Profit after Mitra (all goes to investors, no investment deduction)
        const sisaDividen = profitAfterMitra;
        
        // Dividen Investor = Laba Rugi - Dividen Mitra - Dividen Manager (if any)
        // = profitLoss minus ALL shareholder dividends (regardless of position)
        const totalShareholderDividend = shareholdersWithDividend.reduce((sum, sh) => sum + (sh.dividend || 0), 0);
        const investorDividend = profitLoss - totalShareholderDividend;
        
        // Determine PRIMARY manager
        const primaryManager = managersInBranch.length > 0 
          ? managersInBranch.reduce((prev, current) => 
              (current.percentage > prev.percentage) ? current : prev
            )
          : null;

        return {
          branchId: branch.id,
          branchName: branch.name,
          profitLoss: profitLoss,
          shareholders: shareholdersWithDividend,
          totalSharePercentage: totalSharePercentage,
          mitraDividend: mitraDividend,
          managersWithShares: managersInBranch,
          primaryManager: primaryManager,
          totalManagerPercentage: totalManagerPercentage,
          managerDividend: managerDividend,
          sisaDividen: sisaDividen,
          investorDividend: investorDividend,
          hasManager: managersInBranch.length > 0
        };
      });

      // For CLOSED periods with a snapshot, show the frozen dividend table instead of
      // recomputing it: saham mitra bisa berubah di periode berikutnya, dan laba live
      // ikut bergeser saat stok akhir terjual di periode setelahnya.
      const snapshotBranchRows = (snapshotData.has_snapshot && Array.isArray(snapshotData.branch_dividends) && snapshotData.branch_dividends.length > 0)
        ? snapshotData.branch_dividends.map((b) => {
            const perMitra = b.mitra_dividend_per_mid || {};
            const perManager = b.manager_dividend_per_mid || {};
            const shareholders = (b.shareholders || []).map((sh) => ({
              manager_id: sh.manager_id,
              manager_name: sh.manager_name,
              percentage: sh.percentage,
              position: sh.position,
              dividend: perMitra[sh.manager_id] ?? perManager[sh.manager_id] ?? 0,
            }));
            const managersWithShares = shareholders.filter((sh) => sh.position === 'Manager');
            const totalShareholderDividend = shareholders.reduce((sum, sh) => sum + (sh.dividend || 0), 0);
            return {
              branchId: b.branch_id,
              branchName: b.branch_name,
              profitLoss: b.profit_loss || 0,
              shareholders,
              totalSharePercentage: shareholders.reduce((sum, sh) => sum + (sh.percentage || 0), 0),
              mitraDividend: b.mitra_dividend || 0,
              managersWithShares,
              primaryManager: managersWithShares.length > 0
                ? managersWithShares.reduce((prev, cur) => (cur.percentage > prev.percentage ? cur : prev))
                : null,
              totalManagerPercentage: b.total_manager_percentage || 0,
              managerDividend: Object.values(perManager).reduce((sum, v) => sum + (v || 0), 0),
              sisaDividen: b.sisa_dividen || 0,
              investorDividend: (b.profit_loss || 0) - totalShareholderDividend,
              hasManager: !!b.has_manager,
            };
          })
        : null;

      if (snapshotBranchRows) {
        // Laba PUSAT ikut dibekukan: total_investor_dividend = pusat + Σ sisa_dividen - pembagian_manager
        const snapSisa = snapshotBranchRows.reduce((sum, b) => sum + b.sisaDividen, 0);
        pusatRow.profitLoss = (snapshotData.total_investor_dividend || 0) - snapSisa + (snapshotData.pembagian_manager || 0);
        pusatRow.investorDividend = pusatRow.profitLoss;
      }

      // Urutan baris mengikuti urutan di menu Cabang: status (ACTIVE -> INACTIVE -> BLACKLIST),
      // lalu order_number, lalu nama. Penting untuk periode tertutup karena urutan snapshot
      // mengikuti saat periode ditutup, bukan urutan cabang saat ini.
      const statusOrder = { ACTIVE: 1, INACTIVE: 2, BLACKLIST: 3 };
      const branchSortKey = {};
      branchesData.forEach((b) => {
        branchSortKey[b.id] = [
          statusOrder[b.status || 'ACTIVE'] || 4,
          b.order_number || 9999,
          b.name || '',
        ];
      });
      const compareBranchRows = (a, b) => {
        const ka = branchSortKey[a.branchId] || [5, 9999, a.branchName || ''];
        const kb = branchSortKey[b.branchId] || [5, 9999, b.branchName || ''];
        if (ka[0] !== kb[0]) return ka[0] - kb[0];
        if (ka[1] !== kb[1]) return ka[1] - kb[1];
        return String(ka[2]).localeCompare(String(kb[2]));
      };

      const dividendRows = [...(snapshotBranchRows || dividendData)].sort(compareBranchRows);

      // Separate branches with and without managers
      const withManager = dividendRows.filter(b => b.hasManager);
      const withoutManager = dividendRows.filter(b => !b.hasManager);
      
      // Group branches with manager by PRIMARY manager name
      // Each branch only belongs to ONE manager (the primary one with highest ownership)
      const branchesByManager = {};
      withManager.forEach(branch => {
        const managerName = branch.primaryManager?.manager_name || 'Manager';
        if (!branchesByManager[managerName]) {
          branchesByManager[managerName] = [];
        }
        branchesByManager[managerName].push(branch);
      });
      
      // Insert PUSAT row at the beginning of branches without manager
      withoutManager.unshift(pusatRow);

      // Store in separate state variables
      setBranchesWithManager(withManager);
      setBranchesWithoutManager(withoutManager);
      setBranchesByManager(branchesByManager); // NEW: Store grouped branches
      
      // Keep all data in original array for backward compatibility
      const allBranchData = [...dividendRows];
      allBranchData.unshift(pusatRow);
      setBranchDividends(allBranchData);

      // Calculate Laba Ditahan from dividend table
      const totalMitraDividend = dividendRows.reduce((sum, b) => sum + b.mitraDividend, 0);
      
      // Calculate Pembagian Manager from TOTAL Sisa Dividen (branches with manager only)
      const totalSisaDividenManager = withManager.reduce((sum, b) => sum + b.sisaDividen, 0);
      const managerPercentage = withManager.length > 0 && withManager[0].managersWithShares && withManager[0].managersWithShares.length > 0 
        ? withManager[0].managersWithShares[0].percentage 
        : 0;
      const pembagianManager = Math.round((managerPercentage / 100) * totalSisaDividenManager);
      
      // Laba Ditahan (Mitra) = Total Dividen Mitra + Pembagian Manager
      // Laba Ditahan (Investor) = Total Dividen Investor (non-manager) + Sisa Dividen setelah Manager
      // For CLOSED periods with a snapshot, use the frozen snapshot totals. Live totals keep
      // moving after closing (e.g. stok akhir yang dijual di periode berikutnya), while ASET
      // sudah dibekukan dari snapshot — memakai angka live membuat neraca tidak seimbang.
      const totalDividenInvestorNonManager = withoutManager.reduce((sum, b) => sum + b.investorDividend, 0);
      const sisaDividenAfterManager = totalSisaDividenManager - pembagianManager;
      const useDividendSnapshot = snapshotData.has_snapshot
        && snapshotData.total_mitra_dividend !== undefined && snapshotData.total_mitra_dividend !== null
        && snapshotData.total_investor_dividend !== undefined && snapshotData.total_investor_dividend !== null;

      const labaDitahanCabang = useDividendSnapshot
        ? snapshotData.total_mitra_dividend + (snapshotData.pembagian_manager || 0)
        : totalMitraDividend + pembagianManager;
      const labaDitahanInvestor = useDividendSnapshot
        ? snapshotData.total_investor_dividend
        : totalDividenInvestorNonManager + sisaDividenAfterManager;
      
      // Calculate Total Investasi Mitra.
      // For CLOSED periods with a snapshot, use the frozen snapshot value so it does not
      // follow live data that changes as later periods add more investments.
      let totalInvestasiMitra;
      if (snapshotData.has_snapshot && snapshotData.total_investasi_mitra !== undefined && snapshotData.total_investasi_mitra !== null) {
        totalInvestasiMitra = snapshotData.total_investasi_mitra;
      } else {
        totalInvestasiMitra = managersData.reduce((sum, m) => sum + (m.total_investment_amount || 0), 0);
      }

      // Update totalEquity with split retained earnings + investasi mitra
      const totalEquity = investorCapital + totalInvestasiMitra + labaDitahanCabang + labaDitahanInvestor;

      // Balance check: Assets = Liabilities + Equity
      const balanceDifference = totalAssets - (totalLiabilities + totalEquity);

      // Calculate inventory details per branch
      const inventoryByBranch = branchesData.map(branch => {
        const branchInventorySales = sales.filter(s => 
          s.branch_id === branch.id && s.keterangan === 'Stok'
        );
        
        const inventoryAmount = branchInventorySales.reduce((sum, s) => sum + (s.total_amount || 0), 0);
        const inventoryWeight = branchInventorySales.reduce((sum, s) => sum + (s.quantity_kg || 0), 0);
        
        return {
          branchId: branch.id,
          branchName: branch.name,
          inventoryAmount,
          inventoryWeight,
          itemCount: branchInventorySales.length
        };
      }).filter(inv => inv.inventoryAmount > 0); // Only show branches with inventory
      
      setInventoryDetails(inventoryByBranch);

      setBalanceSheet({
        // Assets - Detailed breakdown (NO MORE Hutang Usaha or Laba Rugi Mitra)
        centralBalance: centralBalance,
        branchBalance: branchBalance,
        customerBalance: customerBalance,
        pendingMoney: pendingMoney,
        cash: totalCash,
        inventory: inventoryValue,
        receivables: receivables,
        totalCurrentAssets: totalCurrentAssets,
        totalAssets: totalAssets,
        
        // Liabilities
        hutangUsaha: hutangUsaha,
        labaRugiMitra: labaRugiMitra,
        totalLiabilities: totalLiabilities,
        
        // Equity
        investorCapital: investorCapital,
        totalInvestasiMitra: totalInvestasiMitra,
        retainedEarnings: retainedEarnings, // Keep original for reference
        labaDitahanCabang: labaDitahanCabang, // New: Laba Ditahan (Cabang)
        labaDitahanInvestor: labaDitahanInvestor, // New: Laba Ditahan (Investor)
        totalEquity: totalEquity,
        
        // Balance Check: threshold 0.005 menyaring float-noise tapi tetap mendeteksi selisih ≥0,01
        balanceDifference: balanceDifference,
        isBalanced: Math.abs(balanceDifference) < 0.005
      });

    } catch (error) {
      console.error('Failed to fetch balance sheet:', error);
      toast.error('Gagal memuat data balance sheet');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) return 'Rp.\u00a00';
    // Show up to 2 decimals when value has a fractional part, so any non-integer
    // rounding artefact is visible to the user.
    const hasFraction = Number(amount) % 1 !== 0;
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: hasFraction ? 2 : 0
    }).format(amount).replace('Rp', 'Rp.');
  };

  // Filter helper: filter branches by search and show-all-zero toggle
  const filterBranchesForLabaRugi = (branchList) => {
    if (!Array.isArray(branchList)) return [];
    const searchLower = (labaRugiSearch || '').trim().toLowerCase();
    return branchList.filter((b) => {
      // Search filter: matches Cabang name OR any Mitra (shareholder) name in this branch
      if (searchLower) {
        const branchName = (b.branchName || '').toLowerCase();
        const mitraNames = Array.isArray(b.shareholders)
          ? b.shareholders.map((sh) => (sh.manager_name || '').toLowerCase())
          : [];
        const matchBranch = branchName.includes(searchLower);
        const matchMitra = mitraNames.some((n) => n.includes(searchLower));
        if (!matchBranch && !matchMitra) return false;
      }
      // Hide branches with 0 profit/loss unless "show all" is checked
      if (!labaRugiShowAll) {
        // Zero check with small tolerance for float
        if (Math.abs(b.profitLoss || 0) < 0.005) return false;
      }
      return true;
    });
  };

  const filteredBranchesWithoutManager = filterBranchesForLabaRugi(branchesWithoutManager);

  // Ringkasan dividen per mitra dari hasil pencarian Laba Rugi
  const searchMitraSummary = (() => {
    if (!(labaRugiSearch || '').trim()) return [];
    const matched = [
      ...filteredBranchesWithoutManager,
      ...Object.values(branchesByManager || {}).flatMap((list) => filterBranchesForLabaRugi(list)),
    ];
    const totals = {};
    matched.forEach((b) => {
      (b.shareholders || []).forEach((sh) => {
        if (!sh.manager_name || !(sh.percentage > 0)) return;
        if (!totals[sh.manager_name]) totals[sh.manager_name] = { name: sh.manager_name, dividend: 0, branches: 0 };
        totals[sh.manager_name].dividend += sh.dividend || 0;
        totals[sh.manager_name].branches += 1;
      });
    });
    return Object.values(totals).sort((a, b) => b.dividend - a.dividend);
  })();

  if (loading) {
    return <LoadingSpinner size="md" color="teal" />;
  }

  if (!balanceSheet) {
    return (
      <div className="text-center py-12 text-slate-500">
        <p>Gagal memuat data balance sheet</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Balance Check Warning — only show after data has fully loaded */}
      {balanceDataReady && (
        <BalanceWarning isBalanced={isActuallyBalanced} difference={actualBalanceDifference} />
      )}

      {/* Balance Sheet Card Component */}
      <BalanceSheetCard
        periodData={periodData}
        balanceSheet={balanceSheet}
        periodTransfers={periodTransfers}
        inventoryDetails={inventoryDetails}
        previousPeriodRetainedEarnings={previousPeriodRetainedEarnings}
        isActuallyBalanced={balanceDataReady ? isActuallyBalanced : true}
        actualBalanceDifference={balanceDataReady ? actualBalanceDifference : 0}
        onInventoryClick={() => setShowInventoryDialog(true)}
        onTransferClick={() => setShowTransferDialog(true)}
      />

      {/* Yearly Sales Summary Table + Modal Chart (8:4 ratio) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8">
          <YearlySummaryTable 
            selectedYear={selectedYear}
            yearlySummary={yearlySummary}
            loadingYearlySummary={loadingYearlySummary}
            selectedPeriod={selectedPeriod}
          />
        </div>
        <div className="lg:col-span-4">
          <YearlyModalChart
            selectedYear={selectedYear}
            yearlySummary={yearlySummary}
            loadingYearlySummary={loadingYearlySummary}
          />
        </div>
      </div>

      {/* Branch Dividend Table - Split into two tables */}
      
      {/* Table 1: Branches WITHOUT Manager (Investor Table - MOVED TO TOP) */}
      <Card className="border-2 border-green-200" ref={labaRugiRef}>
        <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <CardTitle className="text-xl font-bold text-slate-800">
              Laba Rugi{periodData ? ` - ${periodData.name}` : ''}
            </CardTitle>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 md:max-w-[55%] w-full md:w-auto">
              <input
                type="text"
                placeholder="Cari nama cabang / mitra..."
                value={labaRugiSearch}
                onChange={(e) => setLabaRugiSearch(e.target.value)}
                className="flex-1 min-w-[200px] px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                data-testid="laba-rugi-search-input"
              />
              {!capturingLabaRugi && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCaptureLabaRugi}
                  title="Simpan Laba Rugi sebagai Gambar"
                  className="shrink-0 bg-white"
                  data-testid="laba-rugi-capture-btn"
                >
                  <Camera size={18} />
                </Button>
              )}
              <label className="flex items-center gap-2 cursor-pointer select-none whitespace-nowrap" data-testid="laba-rugi-show-all-label">
                <input
                  type="checkbox"
                  checked={labaRugiShowAll}
                  onChange={(e) => setLabaRugiShowAll(e.target.checked)}
                  className="w-4 h-4 cursor-pointer accent-teal-600"
                  data-testid="laba-rugi-show-all-checkbox"
                />
                <span className="text-sm text-slate-700">Show All</span>
              </label>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-green-100 border-b-2 border-green-600">
                  <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">No.</th>
                  <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Cabang</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-slate-700">Laba Rugi</th>
                  <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Mitra</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-slate-700">Saham</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-slate-700">Dividen</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-slate-700">Dividen Investor</th>
                </tr>
              </thead>
              <tbody>
                {filteredBranchesWithoutManager.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-slate-500">
                      Tidak ada data cabang
                    </td>
                  </tr>
                ) : (
                  filteredBranchesWithoutManager.map((branch, index) => (
                    <tr 
                      key={branch.branchId} 
                      className={`border-b hover:bg-slate-50 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
                    >
                      <td className="px-4 py-3 text-sm text-slate-700">{index + 1}</td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{branch.branchName}</td>
                      <td className={`px-4 py-3 text-sm text-right font-semibold ${
                        branch.profitLoss >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formatCurrency(branch.profitLoss)}
                      </td>
                      {/* Mitra Column */}
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {branch.shareholders && branch.shareholders.length > 0 ? (
                          <div>
                            {branch.shareholders.map((sh, idx) => (
                              <div 
                                key={idx} 
                                className={`py-1 ${idx > 0 ? 'border-t border-slate-300' : ''}`}
                              >
                                {sh.manager_name}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      {/* Saham Column - Only percentage, with separator */}
                      <td className="px-4 py-3 text-sm text-center text-slate-700">
                        {branch.shareholders && branch.shareholders.length > 0 ? (
                          <div>
                            {branch.shareholders.map((sh, idx) => (
                              <div 
                                key={idx} 
                                className={`py-1 ${idx > 0 ? 'border-t border-slate-300' : ''} ${sh.percentage > 0 ? '' : 'text-slate-400'}`}
                              >
                                {sh.percentage > 0 ? `${sh.percentage.toFixed(2)}%` : '-'}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      {/* Dividen Column - Only amount, with separator */}
                      <td className="px-4 py-3 text-sm text-right">
                        {branch.shareholders && branch.shareholders.length > 0 ? (
                          <div>
                            {branch.shareholders.map((sh, idx) => (
                              <div 
                                key={idx} 
                                className={`py-1 font-semibold ${idx > 0 ? 'border-t border-slate-300' : ''} ${
                                  sh.percentage > 0 
                                    ? (sh.dividend >= 0 ? 'text-green-600' : 'text-red-600')
                                    : 'text-slate-400'
                                }`}
                              >
                                {sh.percentage > 0 ? formatCurrency(sh.dividend) : '-'}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      {/* Dividen Investor Column */}
                      <td className={`px-4 py-3 text-sm text-right font-semibold ${
                        branch.investorDividend >= 0 ? 'text-blue-600' : 'text-red-600'
                      }`}>
                        {formatCurrency(branch.investorDividend)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {filteredBranchesWithoutManager.length > 0 && (
                <tfoot>
                  <tr className="bg-green-50 border-t-2 border-green-600 font-bold">
                    <td colSpan="2" className="px-4 py-3 text-sm text-slate-800">TOTAL</td>
                    <td className={`px-4 py-3 text-sm text-right ${
                      filteredBranchesWithoutManager.reduce((sum, b) => sum + b.profitLoss, 0) >= 0 
                        ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {formatCurrency(filteredBranchesWithoutManager.reduce((sum, b) => sum + b.profitLoss, 0))}
                    </td>
                    <td className="px-4 py-3"></td>
                    <td className="px-4 py-3"></td>
                    <td className={`px-4 py-3 text-sm text-right ${
                      filteredBranchesWithoutManager.reduce((sum, b) => sum + b.mitraDividend, 0) >= 0 
                        ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {formatCurrency(filteredBranchesWithoutManager.reduce((sum, b) => sum + b.mitraDividend, 0))}
                    </td>
                    <td className={`px-4 py-3 text-sm text-right ${
                      filteredBranchesWithoutManager.reduce((sum, b) => sum + b.investorDividend, 0) >= 0 
                        ? 'text-blue-700' : 'text-red-700'
                    }`}>
                      {formatCurrency(filteredBranchesWithoutManager.reduce((sum, b) => sum + b.investorDividend, 0))}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Ringkasan Total Dividen Mitra dari hasil pencarian */}
      {searchMitraSummary.length > 0 && (
        <Card className="border-2 border-amber-200" data-testid="laba-rugi-search-summary-card">
          <CardHeader className="bg-gradient-to-r from-amber-50 to-yellow-50">
            <CardTitle className="text-lg font-bold text-slate-800">
              Total Dividen Mitra - Hasil Pencarian "{labaRugiSearch}"
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-amber-100 border-b-2 border-amber-600">
                    <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">No.</th>
                    <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Mitra</th>
                    <th className="px-4 py-3 text-center text-sm font-bold text-slate-700">Jumlah Cabang</th>
                    <th className="px-4 py-3 text-right text-sm font-bold text-slate-700">Total Dividen</th>
                  </tr>
                </thead>
                <tbody>
                  {searchMitraSummary.map((m, index) => (
                    <tr
                      key={m.name}
                      className={`border-b hover:bg-slate-50 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
                      data-testid="laba-rugi-search-summary-row"
                      onClick={() => openMitraDialog(m.name)}
                      style={{ cursor: 'pointer' }}
                      title="Klik untuk melihat rincian per partai"
                    >
                      <td className="px-4 py-3 text-sm text-slate-700">{index + 1}</td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{m.name}</td>
                      <td className="px-4 py-3 text-sm text-center text-slate-700">{m.branches}</td>
                      <td className={`px-4 py-3 text-sm text-right font-semibold ${m.dividend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(m.dividend)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-amber-50 border-t-2 border-amber-600 font-bold" data-testid="laba-rugi-search-summary-total">
                    <td colSpan="2" className="px-4 py-3 text-sm text-slate-800">TOTAL</td>
                    <td className="px-4 py-3 text-sm text-center text-slate-800">
                      {searchMitraSummary.reduce((s, m) => s + m.branches, 0)}
                    </td>
                    <td className={`px-4 py-3 text-sm text-right ${
                      searchMitraSummary.reduce((s, m) => s + m.dividend, 0) >= 0 ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {formatCurrency(searchMitraSummary.reduce((s, m) => s + m.dividend, 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={mitraDialog.open} onOpenChange={(open) => !open && setMitraDialog({ open: false, mitra: null, loading: false, rows: [] })}>
        <DialogContent className="max-w-3xl" data-testid="mitra-partai-dialog">
          <DialogHeader>
            <DialogTitle>
              Rincian Dividen per Partai - {mitraDialog.mitra}{periodData ? ` (${periodData.name})` : ''}
            </DialogTitle>
          </DialogHeader>
          {mitraDialog.loading ? (
            <div className="py-10 text-center text-slate-500">Memuat rincian partai...</div>
          ) : (
            <div className="max-h-[65vh] overflow-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b-2 border-amber-600">
                    <th className="px-3 py-2 text-left font-bold text-slate-700">Cabang</th>
                    <th className="px-3 py-2 text-left font-bold text-slate-700">Partai</th>
                    <th className="px-3 py-2 text-right font-bold text-slate-700">Laba Rugi</th>
                    <th className="px-3 py-2 text-right font-bold text-slate-700">Dividen</th>
                  </tr>
                </thead>
                <tbody>
                  {mitraDialog.rows.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-3 py-8 text-center text-slate-500">Tidak ada data partai</td>
                    </tr>
                  ) : mitraDialog.rows.map((g) => (
                    <React.Fragment key={g.branchId}>
                      {g.rows.map((r, idx) => (
                        <tr key={`${g.branchId}-${r.partai}`} className="border-b bg-white" data-testid="mitra-partai-row">
                          <td className="px-3 py-1.5 text-slate-700">
                            {idx === 0 ? `${g.branchName} (${g.percentage.toFixed(2)}%)` : ''}
                          </td>
                          <td className="px-3 py-1.5 text-slate-700">{r.partai}</td>
                          <td className={`px-3 py-1.5 text-right ${r.labaRugi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(r.labaRugi)}
                          </td>
                          <td className={`px-3 py-1.5 text-right font-medium ${r.dividen >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(r.dividen)}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-slate-100 border-b-2 border-slate-300 font-semibold" data-testid="mitra-partai-branch-total">
                        <td colSpan="2" className="px-3 py-1.5 text-slate-800">Total {g.branchName}</td>
                        <td className="px-3 py-1.5 text-right">{formatCurrency(g.rows.reduce((s, r) => s + r.labaRugi, 0))}</td>
                        <td className="px-3 py-1.5 text-right">{formatCurrency(g.rows.reduce((s, r) => s + r.dividen, 0))}</td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
                {mitraDialog.rows.length > 0 && (
                  <tfoot>
                    <tr className="bg-amber-50 border-t-2 border-amber-600 font-bold" data-testid="mitra-partai-grand-total">
                      <td colSpan="2" className="px-3 py-2 text-slate-800">TOTAL KESELURUHAN</td>
                      <td className="px-3 py-2 text-right">
                        {formatCurrency(mitraDialog.rows.reduce((s, g) => s + g.rows.reduce((x, r) => x + r.labaRugi, 0), 0))}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {formatCurrency(mitraDialog.rows.reduce((s, g) => s + g.rows.reduce((x, r) => x + r.dividen, 0), 0))}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tables for Each Manager - Loop through branchesByManager */}      {Object.entries(branchesByManager).map(([managerName, managerBranchesAll]) => {
        const managerBranches = filterBranchesForLabaRugi(managerBranchesAll);
        if (managerBranches.length === 0) return null;
        return (
        <Card key={managerName} className="border-2 border-teal-200">
          <CardHeader className="bg-gradient-to-r from-teal-50 to-blue-50">
            <CardTitle className="text-xl font-bold text-slate-800">
              {/* Dynamic title with manager name */}
              Laba Rugi {managerName}{periodData ? ` - ${periodData.name}` : ''}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-teal-100 border-b-2 border-teal-600">
                    <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">No.</th>
                    <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Cabang</th>
                    <th className="px-4 py-3 text-right text-sm font-bold text-slate-700">Laba Rugi</th>
                    <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Mitra</th>
                    <th className="px-4 py-3 text-center text-sm font-bold text-slate-700">Saham</th>
                    <th className="px-4 py-3 text-right text-sm font-bold text-slate-700">Dividen</th>
                    <th className="px-4 py-3 text-right text-sm font-bold text-slate-700">Sisa Dividen</th>
                  </tr>
                </thead>
                <tbody>
                  {managerBranches.map((branch, index) => (
                    <tr 
                      key={branch.branchId} 
                      className={`border-b hover:bg-slate-50 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
                    >
                      <td className="px-4 py-3 text-sm text-slate-700">{index + 1}</td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{branch.branchName}</td>
                      <td className={`px-4 py-3 text-sm text-right font-semibold ${
                        branch.profitLoss >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formatCurrency(branch.profitLoss)}
                      </td>
                      {/* Create merged array: non-manager shareholders only */}
                      {(() => {
                        const nonManagerShareholders = (branch.shareholders || []).filter(sh => {
                          const manager = managers.find(m => m.id === sh.manager_id);
                          return manager && manager.position !== 'Manager';
                        });
                        
                        return (
                          <>
                            {/* Mitra Column */}
                            <td className="px-4 py-3 text-sm text-slate-700">
                              {nonManagerShareholders.length > 0 ? (
                                <div>
                                  {nonManagerShareholders.map((sh, idx) => (
                                    <div 
                                      key={idx} 
                                      className={`py-1 ${idx > 0 ? 'border-t border-slate-300' : ''}`}
                                    >
                                      {sh.manager_name}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            
                            {/* Saham Column */}
                            <td className="px-4 py-3 text-sm text-center text-slate-700">
                              {nonManagerShareholders.length > 0 ? (
                                <div>
                                  {nonManagerShareholders.map((sh, idx) => (
                                    <div 
                                      key={idx} 
                                      className={`py-1 ${idx > 0 ? 'border-t border-slate-300' : ''} ${sh.percentage > 0 ? '' : 'text-slate-400'}`}
                                    >
                                      {sh.percentage > 0 ? `${sh.percentage.toFixed(2)}%` : '-'}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            
                            {/* Dividen Column */}
                            <td className="px-4 py-3 text-sm text-right">
                              {nonManagerShareholders.length > 0 ? (
                                <div>
                                  {nonManagerShareholders.map((sh, idx) => (
                                    <div 
                                      key={idx} 
                                      className={`py-1 font-semibold ${idx > 0 ? 'border-t border-slate-300' : ''} ${
                                        sh.percentage > 0 
                                          ? (sh.dividend >= 0 ? 'text-green-600' : 'text-red-600')
                                          : 'text-slate-400'
                                      }`}
                                    >
                                      {sh.percentage > 0 ? formatCurrency(sh.dividend) : '-'}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                          </>
                        );
                      })()}
                      {/* Sisa Dividen Column - no separator, single value */}
                      <td className={`px-4 py-3 text-sm text-right font-semibold ${
                        branch.sisaDividen >= 0 ? 'text-blue-600' : 'text-red-600'
                      }`}>
                        {formatCurrency(branch.sisaDividen)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-teal-50 border-t-2 border-teal-600 font-bold">
                    <td colSpan="2" className="px-4 py-3 text-sm text-slate-800">TOTAL</td>
                    <td className={`px-4 py-3 text-sm text-right ${
                      managerBranches.reduce((sum, b) => sum + b.profitLoss, 0) >= 0 
                        ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {formatCurrency(managerBranches.reduce((sum, b) => sum + b.profitLoss, 0))}
                    </td>
                    <td className="px-4 py-3"></td>
                    <td className="px-4 py-3"></td>
                    <td className={`px-4 py-3 text-sm text-right ${
                      managerBranches.reduce((sum, b) => sum + b.mitraDividend, 0) >= 0 
                        ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {formatCurrency(managerBranches.reduce((sum, b) => sum + b.mitraDividend, 0))}
                    </td>
                    <td className={`px-4 py-3 text-sm text-right ${
                      managerBranches.reduce((sum, b) => sum + b.sisaDividen, 0) >= 0 
                        ? 'text-blue-700' : 'text-red-700'
                    }`}>
                      {formatCurrency(managerBranches.reduce((sum, b) => sum + b.sisaDividen, 0))}
                    </td>
                  </tr>
                  
                  {/* Row Pembagian Manager - Manager gets % from TOTAL Sisa Dividen */}
                  <tr className="bg-amber-50 border-t-2 border-amber-400">
                    <td colSpan="2" className="px-4 py-3 text-sm font-bold text-slate-800">
                      PEMBAGIAN MANAGER
                    </td>
                    <td className="px-4 py-3"></td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      {managerName}
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-slate-700">
                      {managerBranches[0]?.managersWithShares?.[0]?.percentage 
                        ? `${managerBranches[0].managersWithShares[0].percentage.toFixed(2)}%` 
                        : '-'}
                    </td>
                    <td className={`px-4 py-3 text-sm text-right font-semibold ${
                      (Math.round((managerBranches[0]?.managersWithShares?.[0]?.percentage || 0) / 100 * managerBranches.reduce((sum, b) => sum + b.sisaDividen, 0))) >= 0 
                        ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatCurrency(Math.round((managerBranches[0]?.managersWithShares?.[0]?.percentage || 0) / 100 * managerBranches.reduce((sum, b) => sum + b.sisaDividen, 0)))}
                    </td>
                    <td className={`px-4 py-3 text-sm text-right font-semibold ${
                      (managerBranches.reduce((sum, b) => sum + b.sisaDividen, 0) - Math.round((managerBranches[0]?.managersWithShares?.[0]?.percentage || 0) / 100 * managerBranches.reduce((sum, b) => sum + b.sisaDividen, 0))) >= 0 
                        ? 'text-blue-600' : 'text-red-600'
                    }`}>
                      {formatCurrency(managerBranches.reduce((sum, b) => sum + b.sisaDividen, 0) - Math.round((managerBranches[0]?.managersWithShares?.[0]?.percentage || 0) / 100 * managerBranches.reduce((sum, b) => sum + b.sisaDividen, 0)))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
        );
      })}

      {/* Transfer Dialog Component */}
      <TransferDialog
        open={showTransferDialog}
        onClose={() => {
          setShowTransferDialog(false);
          setSelectedTransferDetail(null);
          setBranchSearchFilter('');
        }}
        periodTransfers={periodTransfers}
        branches={branches}
        searchFilter={branchSearchFilter}
        onSearchChange={setBranchSearchFilter}
        selectedDetail={selectedTransferDetail}
        onSelectDetail={setSelectedTransferDetail}
        selectedPeriod={selectedPeriod}
        onRefresh={async () => {
          await fetchBalanceSheet(selectedPeriod);
          await fetchPeriodData(selectedPeriod);
        }}
      />

      {/* Inventory Dialog Component */}
      <InventoryDialog
        open={showInventoryDialog}
        onClose={() => {
          setShowInventoryDialog(false);
          setInventorySearchFilter('');
        }}
        inventoryDetails={inventoryDetails}
        balanceSheet={balanceSheet}
        searchFilter={inventorySearchFilter}
        onSearchChange={setInventorySearchFilter}
        selectedPeriod={selectedPeriod}
        onImportStokClick={(detail) => {
          setSelectedTransferDetail(detail);
        }}
      />

      {/* Import Stok Dialog Component */}
      <ImportStokDialog
        open={!!(selectedTransferDetail?.transfers)}
        onClose={() => {
          setSelectedTransferDetail(null);
        }}
        detail={selectedTransferDetail}
        selectedPeriod={selectedPeriod}
        onRefresh={async () => {
          await fetchBalanceSheet(selectedPeriod);
          await fetchPeriodData(selectedPeriod);
          setShowInventoryDialog(false);
        }}
      />
    </div>
  );
};

export default Dashboard;
